import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {IConfig, UnleashClient} from 'unleash-proxy-client';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {User} from '@spartacus/core';
import {isPlatformBrowser} from '@angular/common';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ICountrySelectorService} from '../interfaces/country-selector-service.interface';
import {FeatureToggle} from '../models';
import {ConfigService} from './config.service';
import {serviceProviderTokens} from '../token/tokens';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class FeatureTogglesService {
  unleashClient: UnleashClient = undefined!;
  featureTogglesSubject: Subject<FeatureToggle[]> = new BehaviorSubject<FeatureToggle[]>([]);

  constructor(
    private configService: ConfigService,
    private userAccountFacade: UserAccountFacade,
    @Inject(serviceProviderTokens.COUNTRY_SELECTOR_SERVICE_PROVIDER)
    public countrySelectorService: ICountrySelectorService,
    @Inject(PLATFORM_ID) public platformId: object,
  ) {}

  fetchFeatureToggles(): void {
    if (this.unleashClient || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const unleashConfig: IConfig = {
      url: this.configService.getString('unleashProxy.url'),
      clientKey: this.configService.getString('unleashProxy.clientKey'),
      appName: 'ui',
      refreshInterval: 30, // In s not ms!
    };

    this.unleashClient = new UnleashClient(unleashConfig);

    this.unleashClient.updateContext({
      userId: 'anonymous',
      properties: {
        country: this.countrySelectorService.getCurrentCountry().isocode ?? '',
      },
    });

    this.userAccountFacade
      .get()
      .pipe(untilDestroyed(this))
      .subscribe((user?: User) => {
        this.unleashClient.setContextField('userId', user?.scimUserId || 'anonymous');
      });

    this.unleashClient.start();

    this.unleashClient.on('update', () => {
      this.setFeatureToggles();
    });
  }

  setFeatureToggles(): void {
    this.featureTogglesSubject.next(this.unleashClient.getAllToggles());
  }

  getFeatureToggles(): Observable<FeatureToggle[]> {
    return this.featureTogglesSubject.asObservable();
  }

  isEnabled(featureToggleName: string): Observable<boolean> {
    return this.featureTogglesSubject.asObservable().pipe(
      map((toggles) => toggles.find((toggle) => toggle.name === featureToggleName)),
      // Unleash returns enabled features only, if it is disabled it won't be received
      map((toggle) => !!toggle),
      distinctUntilChanged(),
    );
  }

  getToggleWithVariant(featureToggleName: string): Observable<FeatureToggle | false> {
    return this.featureTogglesSubject.asObservable().pipe(
      map((toggles) => toggles.find((toggle) => toggle.name === featureToggleName)),
      map((toggle) => toggle || false),
    );
  }
}
